import type { MouseEventHandler } from "react";
import { useNavigate } from "react-router-dom";

import * as stylex from "@stylexjs/stylex";

import {
    ComposableSpacing,
    type OmitStyles,
    type PropsWithStyleX,
    colors,
    globalFontStyles,
} from "../../styles/tokens.stylex";

// interface ButtonProps extends OmitStyles<React.ButtonHTMLAttributes<HTMLButtonElement>> {
//     as?: "button";
//     onClick?: MouseEventHandler<HTMLButtonElement>;
// }

// interface AnchorProps extends OmitStyles<React.AnchorHTMLAttributes<HTMLAnchorElement>> {
//     as: "a";
//     href: string;
//     onClick?: MouseEventHandler<HTMLAnchorElement>;
// }

// export type IButton = (ButtonProps | AnchorProps) &
//     PropsWithStyleX & {
//         size: keyof typeof buttonSizes;
//         variant?: keyof typeof variants;
//         isExternal?: boolean;
//         icon?: React.ReactNode;
//         onClick?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
//     };

interface BaseButtonProps extends PropsWithStyleX {
    size: keyof typeof buttonSizes;
    variant?: keyof typeof variants;
    isExternal?: boolean;
    icon?: React.ReactNode;
}

interface ButtonAsButton extends BaseButtonProps, OmitStyles<React.ButtonHTMLAttributes<HTMLButtonElement>> {
    as: "button";
}

interface ButtonAsAnchor extends BaseButtonProps, OmitStyles<React.AnchorHTMLAttributes<HTMLAnchorElement>> {
    as: "a";
    href: string;
}

export type IButton = ButtonAsButton | ButtonAsAnchor;

const appDomain = process.env.Base_URL;

export default function Button({ variant, styleX, isExternal, icon, size, children, as, ...props }: IButton) {
    const variantStyles = variant ? [variant !== "none" ? styles.shared : null, variants[variant]] : [];
    const navigate = useNavigate();

    const styleProps = [
        !!icon && styles.icon,
        ...variantStyles,
        buttonSizesMapper[size],
        buttonSizes[size],
        styleX,
    ];

    if (as === "a") {
        const { href, rel, onClick, target, ...rest } = props as ButtonAsAnchor;
        let inDomain = false;
        let isHash = false;

        if (!href) {
            console.log("Anchor elements must have an href attribute", props);
        } else {
            inDomain = href.startsWith("/") || href.startsWith(appDomain);
            isHash = href.startsWith("#");
        }

        const onCustomClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
            if (onClick) {
                onClick(event);
            }

            if (isHash) {
                const url = new URL(window.location.href);
                url.hash = "";
                window.history.replaceState(null, "", url);
                event.preventDefault();
                const hashChangeEvent = new CustomEvent("hashCustomEventChange", {
                    detail: href.slice(1),
                });
                window.dispatchEvent(hashChangeEvent);
                return false;
            }

            if (!isExternal && inDomain && target !== "_blank") {
                navigate(href.replace(window.location.origin, ""));
                event.preventDefault();
                event.stopPropagation();
                return false;
            }
        };

        return (
            <a
                {...rest}
                {...stylex.props(styleProps)}
                onClick={onCustomClick}
                href={href}
                target={inDomain || isHash ? target : "_blank"}
                rel={inDomain ? rel : "nofollow noopener noreferrer"}
            >
                {icon}
                {children}
            </a>
        );
    }

    if ((props as unknown as ButtonAsAnchor).href) {
        throw new Error("Button elements cannot have an href attribute");
    }

    return (
        <button {...(props as ButtonAsButton)} {...stylex.props(styleProps)}>
            {icon}
            {children}
        </button>
    );
}
const styles = stylex.create({
    icon: {
        gap: ComposableSpacing.s2,
        alignContent: "center",
        alignItems: "center",
        display: "inline-flex",
    },
    shared: {
        borderRadius: "1.86em",
        gap: "0.625rem",

        paddingBlock: "0.66em",
        paddingInline: "1.66em",
        transition: "150ms box-shadow ease-in-out",
        backgroundColor: "transparent",
        outlineColor: "transparent",
        outlineOffset: "-0.125rem",
        outlineStyle: "solid",
        outlineWidth: "0.125rem",
        transitionProperty: "box-shadow, color, opacity, font-weight",
    },
});

const variants = stylex.create({
    none: {
        outline: "none",
    },
    magentaRound: {
        backgroundColor: colors.bg_BrandMagenta,
        boxShadow: { default: null, ":hover": `0 0 0.3em ${colors.bg_BrandMagenta}` },
        color: colors.bg_Primary,
        outlineColor: colors.bg_BrandMagenta,
    },
    secondary: {
        backgroundColor: colors.bg_Primary,
        boxShadow: { default: null, ":hover": `0 0 0.3em ${colors.bg_BrandMagenta}` },
        color: colors.bg_BrandMagenta,
        outlineColor: colors.bg_BrandMagenta,
    },
    blue: {
        backgroundColor: colors.bg_BrandPrimary,
        boxShadow: { default: null, ":hover": `0 0 0.3em ${colors.bg_BrandPrimary}` },
        color: colors.bg_Primary,
    },

    lightblue: {
        backgroundColor: colors.bg_lightBlue,
        boxShadow: { default: null, ":hover": `0 0 0.3em ${colors.bg_lightBlue}` },
        color: colors.bg_Primary,
    },
    transparent: {
        backgroundColor: "transparent",
        color: colors.fg_BrandMagenta,
    },

    disabled: {
        backgroundColor: colors.fg_Disabled,
        color: colors.bg_Primary,
        outlineColor: colors.fg_Disabled,
    },
});

const buttonSizes = stylex.create({
    None: {
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
    },
    XS: {
        paddingBottom: "0.375rem",
        paddingLeft: "0.375rem",
        paddingRight: "0.375rem",
        paddingTop: "0.375rem",
    },
    S: {
        paddingBottom: ComposableSpacing.s2,
        paddingLeft: ComposableSpacing.s3,
        paddingRight: ComposableSpacing.s3,
        paddingTop: ComposableSpacing.s2,
    },
    M: {
        paddingBottom: ComposableSpacing.s2,
        paddingLeft: ComposableSpacing.s3,
        paddingRight: ComposableSpacing.s3,
        paddingTop: ComposableSpacing.s2,
    },

    L: {
        paddingBottom: "0.875rem",
        paddingLeft: ComposableSpacing.s3,
        paddingRight: ComposableSpacing.s3,
        paddingTop: "0.875rem",
    },

    XL: {
        paddingBottom: "1.125rem",
        paddingLeft: ComposableSpacing.s3,
        paddingRight: ComposableSpacing.s3,
        paddingTop: "1.125rem",
    },
});
const buttonSizesMapper = {
    None: null as stylex.StyleXStyles,
    XS: globalFontStyles.BodyM,
    S: globalFontStyles.BodyM,
    M: globalFontStyles.BodyL,
    L: globalFontStyles.BodyL,
    XL: globalFontStyles.BodyXL,
};
